/**
 * Analytics Portal V1 — Event timing insights (Phase 14, plan §4.6 + §11 endpoint 7).
 *
 * Renders only what the server's `timing` block contains: the peak visit interval (already
 * localised to the viewer's reporting timezone by the API, so the date part is shown as sent), the
 * final 24 hours before the event start, and post-event activity. Nothing is computed here, no
 * timing claim is derived and no causation is implied. These are visit counts only. A key the
 * server omits — including when the event has no reliable date — renders nothing at all.
 */
import React from "react";
import { format, parseISO } from "date-fns";
import { KpiCard } from "./KpiCard";

const WINDOWS = [
  { key: "final_24h_visits", label: "Visits in the final 24 hours",
    hint: "Measured against this event's stored start time." },
  { key: "post_event_visits", label: "Visits after the event ended" },
];

const peakLabel = (iso) => {
  try {
    return format(parseISO(String(iso).slice(0, 10)), "EEE d MMM yyyy");
  } catch {
    return null;
  }
};

export const EventTimingCard = ({ timing, rangeSuggestion, onUseSupportedRange }) => {
  if (!timing) return null;
  const windows = WINDOWS.filter((entry) => timing[entry.key]);
  const peak = timing.peak_interval ? peakLabel(timing.peak_interval) : null;
  if (!windows.length && !peak) return null;

  return (
    <section className="space-y-3" data-testid="analytics-event-timing"
             aria-labelledby="analytics-event-timing-heading">
      <div>
        <h2 id="analytics-event-timing-heading"
            className="text-base font-[620] text-foreground md:text-lg">Event timing insights</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Observed activity in the selected period, shown in your reporting timezone. These are visit
          counts only — they do not imply a cause.
        </p>
      </div>

      {peak && (
        <div className="rounded-xl border border-border bg-card px-4 py-3"
             data-testid="analytics-event-timing-peak">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Busiest day</p>
          <p className="mt-0.5 text-base font-[620] text-foreground">{peak}</p>
        </div>
      )}

      {windows.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {windows.map((entry) => (
            <KpiCard key={entry.key} metricKey={`timing_${entry.key}`} label={entry.label}
                     hint={entry.hint} metric={timing[entry.key]}
                     rangeSuggestion={rangeSuggestion} onUseSupportedRange={onUseSupportedRange} />
          ))}
        </div>
      )}
    </section>
  );
};
